import { ShiftConfirmationModal } from "@senciamatch/frontend/components/ShiftConfirmationModal";
import { formatBuildingShift } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import { useSidebarMenuStore } from "@senciamatch/frontend/stores/useSidebarMenuStore";
import { type SimpleEditSession, useWorkShiftEditStore } from "@senciamatch/frontend/stores/useWorkShiftEditStore";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const ModifySidebar = () => {
  const { setModifySidebarIsOpen } = useSidebarMenuStore();
  const { sessions, currentSessionId, setCurrentSession, addSession, clearSessions } = useWorkShiftEditStore();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();

  // 現在のworkShiftGroupを取得（フォールバック用）
  const { data: workShiftGroup } = trpc.getWorkShiftGroup.useQuery({ id: params.id || "" }, { enabled: !!params.id });

  // 確認モーダルの状態
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmingSession, setConfirmingSession] = useState<SimpleEditSession | null>(null);
  const [confirmingSessionIndex, setConfirmingSessionIndex] = useState(0);

  // セッション切り替え
  const handleSessionChange = (sessionId: string) => {
    setCurrentSession(sessionId);
  };

  // 現在のworkShiftGroupIdを取得する関数
  const getCurrentWorkShiftGroupId = (): string | null => {
    // マッチング詳細ページ (/matchings/:id) の場合、URLパラメータがworkShiftGroupId
    if (location.pathname.match(/^\/matchings\/[^/]+$/)) {
      return params.id || null;
    }

    // 既存のセッションがある場合は、そこからworkShiftGroupIdを取得
    if (sessions.length > 0) {
      return sessions[0].workShiftGroupId;
    }

    // APIから取得したworkShiftGroupを使用
    if (workShiftGroup?.id) {
      return workShiftGroup.id;
    }

    return null;
  };

  // 新しいセッションを作成
  const createNewSession = (workShiftGroupId: string): SimpleEditSession => {
    const now = new Date();
    return {
      id: `session-${Date.now()}`,
      workShiftGroupId,
      changes: [],
      createdAt: now,
      updatedAt: now,
    };
  };

  // プレビュー機能
  const handlePreview = (session: SimpleEditSession) => {
    navigate(`/matchings/${session.id}/preview`);
  };

  // 新しいセッションを作成
  const handleCreateNew = () => {
    const workShiftGroupId = getCurrentWorkShiftGroupId();

    if (!workShiftGroupId) {
      alert("ワークシフトグループが特定できませんでした。正しいページから操作してください。");
      return;
    }

    // 異なるworkShiftGroupIdのセッションが存在する場合は確認
    const differentGroupSessions = sessions.filter((s) => s.workShiftGroupId !== workShiftGroupId);
    if (differentGroupSessions.length > 0) {
      if (confirm("他のワークシフトグループの変更案があります。リセットして新しい変更案を作成しますか？")) {
        clearSessions();
      } else {
        return;
      }
    }

    const newSession = createNewSession(workShiftGroupId);
    addSession(newSession);
  };

  // 全てのセッションをリセット
  const handleReset = () => {
    if (confirm("全ての変更案をリセットしますか？この操作は取り消せません。")) {
      clearSessions();
    }
  };

  // シフト変更決定の確認
  const handleConfirmShift = (session: SimpleEditSession, sessionIndex: number) => {
    setConfirmingSession(session);
    setConfirmingSessionIndex(sessionIndex);
    setShowConfirmationModal(true);
  };

  // シフト変更確定用のmutation
  const applyShiftChanges = trpc.simpleWorkShiftEdit.applyShiftChanges.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        alert(`${data.appliedCount}件の変更を適用しました`);
      } else {
        alert(`${data.appliedCount}件成功、${data.failedCount}件失敗しました`);
      }
      // モーダルを閉じる
      setShowConfirmationModal(false);
      setConfirmingSession(null);
      setConfirmingSessionIndex(0);

      // セッションをクリアする
      if (confirmingSession) {
        clearSessions();
        // workshiftのshow画面へリダイレクト
        navigate(`/matchings/${confirmingSession.workShiftGroupId}`);
      }
    },
    onError: (error) => {
      alert(`エラーが発生しました: ${error.message}`);
    },
  });

  // シフト変更確定
  const handleShiftConfirmed = () => {
    if (confirmingSession) {
      const changes = confirmingSession.changes.map((change) => ({
        workShiftId: change.workShiftId,
        newStaffShiftId: change.newStaffShiftId,
      }));

      applyShiftChanges.mutate({
        workShiftGroupId: confirmingSession.workShiftGroupId,
        changes,
      });
    }
  };

  // 確認モーダルを閉じる
  const handleCloseConfirmation = () => {
    setShowConfirmationModal(false);
    setConfirmingSession(null);
    setConfirmingSessionIndex(0);
  };

  return (
    <div className="h-full flex flex-col">
      {/* デバッグ情報 */}
      <div className="text-xs text-gray-500 mb-2">
        現在のセッション: {currentSessionId || "なし"} / セッション数: {sessions.length}
      </div>

      {/* ヘッダー部分 */}
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-lg font-semibold">シフト変更一覧</h2>
        <button
          type="button"
          onClick={() => setModifySidebarIsOpen(false)}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="閉じる"
        >
          <svg
            className="w-5 h-5 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* セッション一覧 */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-20">
        {sessions.length > 0 ? (
          sessions.map((session, index) => (
            <div
              key={session.id}
              className={`w-full text-left border rounded-lg overflow-hidden transition-all ${
                currentSessionId === session.id ? "border-green-500 border-2" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {/* セッション選択ボタン */}
              <button
                type="button"
                className="w-full text-left cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                onClick={() => handleSessionChange(session.id)}
              >
                {/* セッションヘッダー */}
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">シフト変更案 {index + 1}</h3>
                    <div className="text-xs text-gray-500">{new Date(session.updatedAt).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* セッション詳細情報 */}
                <div className="w-full px-4 py-3 pb-0 text-left">
                  {session.changes && session.changes.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">変更履歴 ({session.changes.length}件)</div>
                      {session.changes.map((change, changeIndex) => (
                        <div key={`${change.workShiftId}-${changeIndex}`} className="text-xs bg-gray-50 rounded p-2">
                          <div className="text-gray-600">
                            {formatBuildingShift({
                              year: change.workShiftInfo.year,
                              month: change.workShiftInfo.month,
                              day: change.workShiftInfo.day,
                              buildingName: change.workShiftInfo.buildingName || "",
                              startHour: change.workShiftInfo.startHour ?? 0,
                              startMinute: change.workShiftInfo.startMinute ?? 0,
                              endHour: change.workShiftInfo.endHour ?? 0,
                              endMinute: change.workShiftInfo.endMinute ?? 0,
                            })}
                          </div>
                          <div className="mt-1">
                            <span className="text-gray-500">{change.originalStaffName || "未割当"}</span>
                            <span className="mx-1">→</span>
                            <span className="text-green-600 font-medium">{change.newStaffName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">まだ変更がありません</div>
                  )}
                </div>
              </button>

              {/* アクションボタン */}
              <div className="border-t">
                <div className="flex border-t">
                  <button
                    type="button"
                    onClick={() => handlePreview(session)}
                    className="flex-1 px-4 py-3 text-gray-700 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors border-r"
                  >
                    プレビュー
                  </button>
                  <button
                    type="button"
                    onClick={() => handleConfirmShift(session, index)}
                    className="flex-1 px-4 py-3 text-gray-700 text-sm hover:bg-green-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                  >
                    決定
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 mt-8">
            <p>シフト変更案がありません</p>
            <p className="text-sm mt-2">新しいシフト変更を開始してください</p>
          </div>
        )}
      </div>

      {/* 固定フッター */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t p-4">
        <button
          type="button"
          onClick={handleCreateNew}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          新しいシフト変更案を作成
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full mt-2 text-sm text-gray-500 hover:text-red-600 focus:outline-none focus:text-red-600 transition-colors"
        >
          リセット
        </button>
      </div>

      {/* シフト変更確認モーダル */}
      <ShiftConfirmationModal
        show={showConfirmationModal}
        onClose={handleCloseConfirmation}
        onConfirm={handleShiftConfirmed}
        session={confirmingSession}
        sessionIndex={confirmingSessionIndex}
        isLoading={applyShiftChanges.isPending}
      />
    </div>
  );
};
